import CustomReusableModal from "@iewccouk/Components/CustomReusableModal";
import changeOrder from "@iewccouk/Store/Handlers/CustomModals/ChangeOrder";
import returnRequest from "@iewccouk/Store/Handlers/CustomModals/ReturnRequest";
import showHideCustomReusableModal from "@iewccouk/Store/Handlers/CustomModals/ShowHideCustomReusableModal";
import loadSystemLists from "@iewccouk/Store/Handlers/LoadSystemLists";
import { setGA4Event } from "@iewccouk/Store/Handlers/Utilities/CustomTracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import exportOrders from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/ExportOrders";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import selectOrders from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/SelectOrders";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
    showOrderType = "showOrderType",
    showOrderDocuments = "showOrderDocuments",
}

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    webOrderNumber: ["webOrderNumber"],
    orderDate: ["orderDate", "erpOrderNumber", "webOrderNumber"],
    orderTotal: ["orderTotal"],
    status: ["status"],
    stCompanyName: ["stCompanyName"],
    customerPO: ["customerPO"],
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
        [fields.showOrderType]: boolean;
        [fields.showOrderDocuments]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        showAddedToCartModal: (getSettingsCollection(state) as any).customCartSettings?.showCustomAddToCartModal === true,
        language: state.context.session.language,
        showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
        isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
        selectedOrderIds: state.pages.orderHistory.selectedOrderIds || {},
        showPricing: state.context.showPricing,
        ordersDetailURL: getPageLinkByPageType(state, "OrderDetailsPage")?.url,
        // Verndale Customization
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
        billTo: getBillToState(state, state.context?.session?.billToId).value,
        hideActionButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideActionButtons,
        hideDownloadDocumentButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideDownloadDocumentButtons,
        hideStateRegion: (state.context.settings.settingsCollection as any).myAccountSettings?.hideStateRegion,
        shouldLoadSystemLists:
            !Array.isArray((state as any).customSystemList?.["return-request"]) ||
            !Array.isArray((state as any).customSystemList?.["change-order"]),
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    reorder,
    selectOrders,
    exportOrders,

    /* Verndale Custom Feature - New methods */
    loadSystemLists,
    showHideCustomReusableModal,
    returnRequest,
    changeOrder,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

interface State {
    isProcessing: boolean;
    processingOrderNumber?: string;
}

export interface OrderHistoryTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
    action: any;

    /* Verndale Custom Feature - New styles */
    actionSelector?: SelectPresentationProps;
}

export const orderHistoryTableStyles: OrderHistoryTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    action: {
        css: css`
            position: relative;

            &::after {
                content: "...";
                pointer-events: none;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -65%);
            }
        `,
    },
};

const styles = orderHistoryTableStyles;

class OrderHistoryTable extends React.Component<Props, State> {
    static contextType = OrdersDataViewContext;
    declare context: React.ContextType<typeof OrdersDataViewContext>;

    /* Verndale Custom Feature - Add constructor to set state on the widget */
    constructor(props: Props) {
        super(props);
        this.state = {
            isProcessing: false,
        };
    }

    /* Verndale Custom Feature - Load system lists on client only to avoid SSR promise loops */
    componentDidMount() {
        const {
            shellContext: { isInShell },
            shouldLoadSystemLists,
        } = this.props;
        if (!isInShell && shouldLoadSystemLists) {
            this.props.loadSystemLists({ namelist: "ReturnRequestOrderResponse,ChangeOrderResponse" });
        }
    }

    headerClick(sortField: string) {
        const previousSort = this.props.parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        this.props.updateSearchFields({ sort });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!this.props.parameter.sort) {
            return sorted;
        }
        const [primaryField] = this.props.parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    /* Verndale Custom Feature - Set is processing state based on the order number */
    handleIsProcessingState = (value: boolean, processingOrderNumber?: string) => {
        this.setState({
            isProcessing: value,
            processingOrderNumber,
        });
    };

    /* Verndale Custom Feature - Trigger is processing flag */
    reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        this.handleIsProcessingState(true, orderNumber);
        
        // Verndale Code
        if(this.props.isAuthenticated){
            const sapAccountNumber =
            this.props.isAuthenticated && this.props.billTo
                ? this.props.billTo.customerNumber.replace(/^0+/, "")
                : "__IS_LOADING__";        
                setGA4Event([], "reorder", {sapAccountNumber});
        } 

        this.props.reorder({
            orderNumber,
            onSuccess: () => {
                this.onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder);
                this.handleIsProcessingState(false);
            },
            onError: message => {
                this.onReorderError(message);
                this.handleIsProcessingState(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    };

    onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!this.props.showAddToCartConfirmationDialog || this.props.showAddedToCartModal) {
            return;
        }
        this.props.toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history: this.props.history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    onReorderError = (message: string) => {
        this.props.toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    parseOrderType = (orderType = "") => {
        switch (orderType.toUpperCase()) {
            case "CARS":
                return translate("AIR Order");
            case "EDI":
                return translate("EDI Order");
            case "OCR":
                return translate("Sales Rep");
            case "WEB":
                return translate("Web Order");
            case "SA":
                return translate("Scheduled Order");
            default:
                return translate("Sales Rep");
        }
    };

    handleAction = (value: string, data: any) => {
        switch (value) {
            case "change":
                this.props.showHideCustomReusableModal({
                    orderNumber: data.orderNumber,
                    key: "change-order",
                    show: true,
                });
                break;
            case "reorder":
                this.reorderClick(data.orderNumber, data.linkOrderNumber, data.isVmiOrder);
                break;
            case "return":
                this.props.showHideCustomReusableModal({
                    orderNumber: data.orderNumber,
                    key: "return-request",
                    show: true,
                });
                break;
        }
    };

    render() {
        const ordersDataView = this.context;
        if (ordersDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!ordersDataView.value) {
            return null;
        }

        if (ordersDataView.value.length === 0) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                        {translate("No orders found")}
                    </Typography>
                </StyledWrapper>
            );
        }

        const rows = ordersDataView.value.map(order => {
            return {
                id: order.id,
                linkOrderNumber: order.erpOrderNumber || order.webOrderNumber,
                date: order.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(order.orderDate),
                          language: this.props.language,
                      })
                    : "",
                orderNumber: order.erpOrderNumber || order.webOrderNumber,
                shipTo: [
                    order.stCompanyName,
                    order.stAddress1,
                    order.stAddress2,
                    order.shipToCity,
                    !this.props.hideStateRegion ? order.shipToState : undefined,
                ]
                    .filter(Boolean)
                    .join(" "),
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                isVmiOrder: !!order.vmiLocationId,
                orderType: this.parseOrderType(order.properties?.orderChannel),
            };
        });

        const {
            showOrderNumber,
            showDate,
            showOrderTotal,
            showStatus,
            showShipTo,
            showPONumber,
            showReorderProducts,
            showOrderType,
            showOrderDocuments,
        } = this.props.fields;

        const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

        const checkboxChangeHandler = (value: boolean, id?: string) => {
            if (id) {
                this.props.selectOrders({ ids: [id] });
            } else {
                let ids: string[] = [];
                if (value) {
                    for (const row of this.context.value!) {
                        if (!this.props.selectedOrderIds[row.id]) {
                            ids.push(row.id);
                        }
                    }
                } else {
                    ids = Object.keys(this.props.selectedOrderIds);
                }

                this.props.selectOrders({ ids });
            }
        };

        const handleExportButtonClick = (selectedOnly = false) => {
            this.props.exportOrders({ ids: selectedOnly ? this.props.selectedOrderIds : {} });
        };

        return (
            <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                {rows.length > 0 && this.props.isVmiOrderHistoryPage && (
                    <GridContainer {...styles.headerContainer}>
                        <GridItem {...styles.gridItem}>
                            <>
                                <Link
                                    disabled={Object.keys(this.props.selectedOrderIds).length === 0}
                                    onClick={() => handleExportButtonClick(true)}
                                >
                                    {translate("Export Selected")}
                                </Link>
                                <Link {...styles.exportLink} onClick={() => handleExportButtonClick()}>
                                    {translate("Export All")}
                                </Link>
                            </>
                        </GridItem>
                    </GridContainer>
                )}
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        {this.props.isVmiOrderHistoryPage && (
                            <DataTableHeader {...styles.checkboxHeader}>
                                <Checkbox
                                    {...styles.rowCheckbox}
                                    checked={
                                        Object.keys(this.props.selectedOrderIds).length === ordersDataView.value.length
                                    }
                                    onChange={(e, value) => checkboxChangeHandler(value)}
                                />
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showDate) && (
                            <DataTableHeader
                                {...styles.orderDateHeader}
                                sorted={this.sorted("orderDate")}
                                onSortClick={() => this.headerClick("orderDate")}
                                data-test-selector="tableHeader_OrderDate"
                            >
                                {translate("Date", "orderDate")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showOrderNumber) && (
                            <DataTableHeader
                                {...styles.orderNumberHeader}
                                title={translate("Order Number")}
                                sorted={this.sorted("webOrderNumber")}
                                onSortClick={() => this.headerClick("webOrderNumber")}
                                data-test-selector="tableHeader_webOrderNumber"
                            >
                                {siteMessage("OrderHistory_Order_Label")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showShipTo) && (
                            <DataTableHeader
                                {...styles.shipToHeader}
                                sorted={this.sorted("stCompanyName")}
                                onSortClick={() => this.headerClick("stCompanyName")}
                            >
                                {translate("Ship To / Pick Up", "stCompanyName")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showStatus) && (
                            <DataTableHeader
                                {...styles.statusHeader}
                                sorted={this.sorted("status")}
                                onSortClick={() => this.headerClick("status")}
                                data-test-selector="tableHeader_status"
                            >
                                {translate("Status", "status")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showOrderType) && (
                            <DataTableHeader>{translate("Order type", "orderType")}</DataTableHeader>
                        )}
                        {isTrueOrUndefined(showPONumber) && this.props.showPoNumber && (
                            <DataTableHeader
                                {...styles.customerPOHeader}
                                title={translate("Purchase Order Number")}
                                sorted={this.sorted("customerPO")}
                                onSortClick={() => this.headerClick("customerPO")}
                            >
                                {siteMessage("OrderHistory_PO_Label")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showOrderTotal) && this.props.showPricing && (
                            <DataTableHeader
                                {...styles.orderTotalHeader}
                                sorted={this.sorted("orderTotal")}
                                onSortClick={() => this.headerClick("orderTotal")}
                            >
                                {translate("Total", "orderTotal")}
                            </DataTableHeader>
                        )}
                        {isTrueOrUndefined(showOrderDocuments) && !this.props.hideDownloadDocumentButtons && (
                            <DataTableHeader>{translate("Order Documents")}</DataTableHeader>
                        )}
                        <DataTableHeader>{translate("Actions")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                        {rows.map(
                            ({
                                id,
                                linkOrderNumber,
                                date,
                                orderNumber,
                                shipTo,
                                status,
                                po,
                                total,
                                isVmiOrder,
                                orderType,
                            }) => (
                                <DataTableRow key={id}>
                                    {this.props.isVmiOrderHistoryPage && (
                                        <DataTableCell {...styles.checkboxCells}>
                                            <Checkbox
                                                {...styles.rowCheckbox}
                                                checked={this.props.selectedOrderIds[id]}
                                                onChange={(e, value) => checkboxChangeHandler(value, id)}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showDate) && (
                                        <DataTableCell
                                            {...styles.orderDateCells}
                                            data-test-selector="orderHistoryTable_tableCell_date"
                                        >
                                            <OrderDetailPageTypeLink
                                                title={date}
                                                orderNumber={linkOrderNumber}
                                                isVmiOrder={isVmiOrder}
                                                isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderNumber) && (
                                        <DataTableCell
                                            {...styles.orderNumberCells}
                                            data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                        >
                                            <OrderDetailPageTypeLink
                                                title={orderNumber}
                                                orderNumber={linkOrderNumber}
                                                isVmiOrder={isVmiOrder}
                                                isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                            />
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showShipTo) && (
                                        <DataTableCell {...styles.shipToCells}>{shipTo}</DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showStatus) && (
                                        <DataTableCell
                                            {...styles.statusCells}
                                            data-test-selector="orderHistoryTable_tableCell_status"
                                        >
                                            {status}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderType) && (
                                        <DataTableCell
                                            {...styles.statusCells}
                                            data-test-selector="orderHistoryTable_tableCell_orderType"
                                        >
                                            {orderType}
                                        </DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showPONumber) && this.props.showPoNumber && (
                                        <DataTableCell {...styles.customerPOCells}>{po}</DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderTotal) && this.props.showPricing && (
                                        <DataTableCell {...styles.orderTotalCells}>{total}</DataTableCell>
                                    )}
                                    {isTrueOrUndefined(showOrderDocuments) && !this.props.hideDownloadDocumentButtons && (
                                        <DataTableCell>
                                            <Link
                                                href={`${this.props.ordersDetailURL}?orderNumber=${orderNumber}#orderAck`}
                                            >
                                                {translate("View Documents")}
                                            </Link>
                                        </DataTableCell>
                                    )}
                                    <DataTableCell>
                                        {/* Verndale Custom Feature - Show spinning wheel after re-order action */}
                                        {this.state.isProcessing &&
                                            this.state.processingOrderNumber === orderNumber && (
                                                <LoadingSpinner {...styles.reorderButtonSpinner} />
                                            )}
                                        {/* Verndale Custom Feature - Show spinning wheel after re-order action */}
                                        {this.state.isProcessing &&
                                            this.state.processingOrderNumber !== orderNumber &&
                                            status !== "Cancelled" && (
                                                <StyledWrapper {...styles.action}>
                                                    <Select
                                                        {...styles.actionSelector}
                                                        value=""
                                                        autoComplete="off"
                                                        disabled={true}
                                                    >
                                                        <option value=""></option>
                                                    </Select>
                                                </StyledWrapper>
                                            )}

                                        {/* Verndale Custom Feature - Show spinning wheel after re-order action */}
                                        {!this.state.isProcessing && status !== "Cancelled" && (
                                            <StyledWrapper {...styles.action}>
                                                <Select
                                                    {...styles.actionSelector}
                                                    onChange={e =>
                                                        this.handleAction(e.target.value, {
                                                            orderNumber,
                                                            linkOrderNumber,
                                                            isVmiOrder,
                                                            status,
                                                        })
                                                    }
                                                    value=""
                                                    autoComplete="off"
                                                >
                                                    <option value=""></option>
                                                    {status === "Open" && (
                                                        <option value="change">
                                                            {translate("Change Order")}
                                                            &nbsp;&nbsp;&nbsp;
                                                        </option>
                                                    )}
                                                    {status === "Completed" && isTrueOrUndefined(showReorderProducts) && (
                                                        <option value="reorder">
                                                            {translate("Re-Order")}
                                                            &nbsp;&nbsp;&nbsp;
                                                        </option>
                                                    )}
                                                    {status === "Completed" && (
                                                        <option value="return">
                                                            {translate("Return Order")}
                                                            &nbsp;&nbsp;&nbsp;
                                                        </option>
                                                    )}
                                                </Select>
                                            </StyledWrapper>
                                        )}
                                    </DataTableCell>
                                    {/* {isTrueOrUndefined(showReorderProducts) && !this.props.isVmiOrderHistoryPage && (
                                        <DataTableCell {...styles.reorderCells}>
                                            {this.props.isReordering[orderNumber] ? (
                                                <LoadingSpinner {...styles.reorderButtonSpinner} />
                                            ) : (
                                                <Button
                                                    disabled={
                                                        Object.keys(this.props.isReordering).length > 0 ||
                                                        this.props.isUpdatingCart
                                                    }
                                                    {...styles.reorderButton}
                                                    onClick={() =>
                                                        this.reorderClick(orderNumber, linkOrderNumber, isVmiOrder)
                                                    }
                                                    data-test-selector="orderHistoryTable_Reorder"
                                                >
                                                    {translate("Reorder")}
                                                </Button>
                                            )}
                                        </DataTableCell>
                                    )} */}
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>

                <CustomReusableModal
                    id="change-order"
                    headline={translate("Change Order")}
                    subHeadline={translate("Why would you like to change this order?")}
                    dropdownHint={translate("Select a response")}
                    messageHint={translate("Enter changes needed to your order")}
                    submitLabel={translate("Request Change")}
                    cancelLabel={translate("Cancel")}
                    errorMessage={siteMessage("Change_Message_Error")}
                    submitRequest={this.props.changeOrder}
                ></CustomReusableModal>

                <CustomReusableModal
                    id="return-request"
                    headline={translate("Return Request")}
                    subHeadline={translate("Why would you like to return part or all of this order?")}
                    dropdownHint={translate("Select a response")}
                    messageHint={translate("Enter items to return")}
                    submitLabel={translate("Submit Request")}
                    cancelLabel={translate("Cancel")}
                    errorMessage={siteMessage("ReturnRequest_Message_Error")}
                    submitRequest={this.props.returnRequest}
                ></CustomReusableModal>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withToaster(withHistory(withIsInShell(OrderHistoryTable))) as any) as any,
    definition: {
        group: "Order History",
        displayName: "Search Results Table",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showOrderType,
                displayName: "Order Type",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showOrderDocuments,
                displayName: "Order Documents",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
